import type { NextRequest, NextResponse } from "next/server";
import { isSessionCookie } from "@/lib/supabase/session-cookie";

// The Supabase auth cookie family, in ONE place. Two callers destroy it — the
// middleware gate (a cookie that proved useless) and /auth/reset (the user
// asking for the door) — and they must agree on what "the session cookie"
// means. A second, drifting definition is how half-cleared sessions happen:
// the jar keeps a well-formed corpse and every reader believes in a session
// that cannot work (08-03 incident).

/**
 * Supabase writes the session as `sb-<project-ref>-auth-token`, split into
 * `.0`/`.1` chunks once it outgrows the 4KB cookie limit. Match the family by
 * shape so a chunked cookie can never be half-cleared.
 */
export function isAuthCookie(name: string): boolean {
  return name.startsWith("sb-") && name.includes("-auth-token");
}

/**
 * A page-readable "someone is signed in here" bit, holding no secret. The
 * session cookie is HttpOnly (CASA 2.3.2), so page code can no longer peek at
 * it for the instant signed-in chrome the marketplace paints before the
 * session loads. The value is "1", never a token.
 */
export const SESSION_HINT_COOKIE = "vestra_signed_in";

const SESSION_HINT_OPTIONS = {
  httpOnly: false,
  secure: process.env.NODE_ENV === "production",
  sameSite: "lax" as const,
  path: "/",
  maxAge: 400 * 24 * 60 * 60,
};

interface CookieWrite {
  name: string;
  value: string;
  options?: { maxAge?: number } & Record<string, unknown>;
}

/**
 * The hint write that mirrors a batch of session-cookie writes: on when the
 * batch stores a session, off when it only clears one, nothing when the batch
 * does not touch the session at all (e.g. the PKCE verifier alone). Derived at
 * the one seam that writes the session, so the two cannot drift.
 */
export function sessionHintFor(writes: CookieWrite[]): CookieWrite[] {
  const session = writes.filter((w) => isSessionCookie(w.name));
  if (session.length === 0) return [];
  const stored = session.some(
    (w) => w.value !== "" && (w.options?.maxAge ?? 1) > 0
  );
  return [
    {
      name: SESSION_HINT_COOKIE,
      value: stored ? "1" : "",
      options: stored
        ? SESSION_HINT_OPTIONS
        : { ...SESSION_HINT_OPTIONS, maxAge: 0 },
    },
  ];
}

/** Expire every cookie in the auth family on `response`, and the hint with it. */
export function clearAuthCookies(
  request: NextRequest,
  response: NextResponse
): void {
  for (const { name } of request.cookies.getAll()) {
    if (isAuthCookie(name)) {
      response.cookies.set(name, "", { maxAge: 0, path: "/" });
    }
  }
  if (request.cookies.has(SESSION_HINT_COOKIE)) {
    response.cookies.set(SESSION_HINT_COOKIE, "", { maxAge: 0, path: "/" });
  }
}
